import { Cite } from '@citation-js/core';
import '@citation-js/plugin-csl';
import { ReferenceEntity } from '../types/reference';
import { parseAuthorName } from '../utils/citationFormatter';
import { createFileLogger } from '../../../utils/logger';

/**
 * CSL Formatter - Standards-compliant citation formatting
 * ======================================================
 * 
 * This module implements citation formatting using the citation-js library,
 * which provides CSL (Citation Style Language) support for thousands of
 * academic citation styles.
 * 
 * ## Key Functions
 * 
 * - `formatCitationWithCSL()`: Format in-text citations
 * - `generateBibliographyWithCSL()`: Generate full bibliography
 * - `getAvailableStyles()`: List supported citation styles
 * - `loadCustomCSLStyle()`: Load custom CSL files
 * 
 * ## CSL Support
 * 
 * CSL is the standard format used by reference managers like Zotero, Mendeley,
 * and Papers. It ensures our citations match academic standards exactly.
 * 
 * This is the core formatting engine - citationFormatter.ts provides the
 * user-facing interface that delegates to these functions.
 */

const log = createFileLogger('CSL_FORMATTER');

/**
 * Convert Reference entity to CSL-JSON format
 * CSL-JSON is the standard format for citation processing
 */
function referenceToCSL(ref: ReferenceEntity | any): any {
  const skeleton = ref.skeleton || {};
  
  // Parse authors into CSL format
  const authors = (skeleton.authors || []).map((authorString: string) => {
    const parsed = parseAuthorName(authorString);
    return {
      family: parsed.family,
      given: parsed.given,
      suffix: parsed.suffix
    };
  });
  
  return {
    id: ref._id || ref.id,
    type: skeleton.itemType || 'article-journal',  // Default to journal article
    title: skeleton.title || ref.name || 'Untitled',
    author: authors,
    issued: skeleton.year ? { 'date-parts': [[skeleton.year]] } : undefined,
    'container-title': skeleton.journal,
    volume: skeleton.volume,
    issue: skeleton.issue,
    page: skeleton.pages,
    DOI: skeleton.doi,
    URL: skeleton.url,
    abstract: skeleton.abstract,
    publisher: skeleton.publisher,
    'publisher-place': skeleton.publisherPlace,
    ISBN: skeleton.isbn,
    ISSN: skeleton.issn
  };
}

/**
 * Format in-text citation using citation-js
 */
export async function formatCitationWithCSL(
  references: ReferenceEntity[],
  style: string = 'apa',
  options: {
    format?: 'text' | 'html';
    type?: 'citation' | 'bibliography';
  } = {}
): Promise<string> {
  try {
    const cslData = references.map(referenceToCSL);
    
    console.log('[CSL] Formatting with citation-js:', {
      style,
      refCount: references.length,
      cslData: cslData[0]
    });
    
    const cite = new Cite(cslData);
    
    const formatted = cite.format(options.type || 'citation', {
      format: options.format || 'text',
      template: style,
      lang: 'en-US'
    });
    
    log.info('[CSL] Formatted citation:', {
      style,
      result: formatted.substring(0, 100)
    });
    
    return formatted;
  } catch (error) {
    log.error('[CSL] Formatting failed:', error);
    // Fallback to basic format
    const names = references.map(r => {
      const authors = r.skeleton?.authors || [];
      if (authors.length === 0) return 'Unknown';
      const parsed = parseAuthorName(authors[0]);
      return parsed.family;
    });
    const year = references[0]?.skeleton?.year || 'n.d.';
    return `(${names.join(', ')}, ${year})`;
  }
}

/**
 * Generate full bibliography using citation-js
 */
export async function generateBibliographyWithCSL(
  references: ReferenceEntity[],
  style: string = 'apa',
  format: 'text' | 'html' = 'html'
): Promise<string> {
  try {
    const cslData = references.map(referenceToCSL);
    
    console.log('[CSL] Generating bibliography:', {
      style,
      format,
      refCount: references.length
    });
    
    const cite = new Cite(cslData);
    
    const bibliography = cite.format('bibliography', {
      format,
      template: style,
      lang: 'en-US'
    });
    
    log.info('[CSL] Bibliography generated:', {
      style,
      entryCount: references.length,
      resultLength: bibliography.length
    });
    
    return bibliography;
  } catch (error) {
    log.error('[CSL] Bibliography generation failed:', error);
    throw error;
  }
}

/**
 * Get list of available CSL styles
 */
export function getAvailableStyles(): string[] {
  return [
    'apa',           // APA 7th edition
    'harvard1',      // Harvard
    'vancouver',     // Vancouver (medical)
    'chicago-note-bibliography',  // Chicago
    'mla',           // MLA 9th edition
    'ieee',          // IEEE
    'nature',        // Nature
    'science',       // Science
    'cell',          // Cell
    'ama',           // American Medical Association
    'acs',           // American Chemical Society
    'bibtex'         // BibTeX style
  ];
}

/**
 * Load custom CSL style from URL
 */
export async function loadCustomCSLStyle(styleUrl: string, styleName: string): Promise<void> {
  try {
    const response = await fetch(styleUrl);
    const cslXml = await response.text();
    
    // Register with citation-js
    const config = Cite.plugins.config.get('@csl');
    config.templates.add(styleName, cslXml);
    
    log.info('[CSL] Custom style loaded:', styleName);
  } catch (error) {
    log.error('[CSL] Failed to load custom style:', error);
    throw error;
  }
}

